import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import { useDialogState } from '~/modules/common/hooks';
import RecallResourceRequestDialog from '~/modules/resourcing/common/components/RecallResourceRequestDialog';
import { ReleaseResourceDialog } from '~/modules/resourcing/common/components/ReleaseResourceRequestDialog';
import useReleaseResourceAllocationForRequest from '~/modules/resourcing/common/enhancers/requestStatusHandlers/useReleaseResourceAllocationForRequest';
import { useResourceRequestStatusHandlers } from '~/modules/resourcing/common/enhancers/requestStatusHandlers/useResourceRequestStatusHandlers';
import { useToggleReleaseResourcesDialog } from '~/modules/resourcing/common/hooks';

import { ResourceActionsDialogContext } from './hooks';

const ResourceActionsDialogContextProvider = ({
  children,
  project,
  resourceRequests,
  maxSkillLevel
}) => {
  const [
    resourceActionContextDetails,
    setResourceActionContextDetails
  ] = useState({
    resourceRequestId: null,
    resourceAllocation: null,
    releaseResourceAllocation: null
  });

  const req = resourceActionContextDetails
    ? resourceRequests.find(
        r => r.id === resourceActionContextDetails.resourceRequestId
      )
    : null;

  const {
    open: showRecallResourcesDialog,
    openDialog: openRecallResourcesDialog,
    closeDialog: closeRecallResourcesDialog
  } = useDialogState(false);

  const {
    showReleaseResourcesDialog,
    openReleaseResourcesDialog,
    closeReleaseResourcesDialog
  } = useToggleReleaseResourcesDialog();

  const handleCloseRecallResourceDialog = useCallback(() => {
    closeRecallResourcesDialog();
    setResourceActionContextDetails(null);
  }, [closeRecallResourcesDialog, setResourceActionContextDetails]);

  const handleCloseReleaseResourceDialog = useCallback(() => {
    closeReleaseResourcesDialog();
    setResourceActionContextDetails(null);
  }, [closeReleaseResourcesDialog, setResourceActionContextDetails]);

  const {
    releaseResourceAllocationsForRequest
  } = useReleaseResourceAllocationForRequest({ projectId: project.id });

  const contextValue = useMemo(
    () => ({
      openRecallResourcesDialog,
      openReleaseResourcesDialog,
      setResourceActionContextDetails,
      resourceActionContextDetails,
      maxSkillLevel
    }),
    [
      openRecallResourcesDialog,
      openReleaseResourcesDialog,
      resourceActionContextDetails,
      maxSkillLevel
    ]
  );

  const {
    onMarkResourceRequestAsSubmitted
  } = useResourceRequestStatusHandlers({ resourceRequest: req });

  return (
    <ResourceActionsDialogContext.Provider value={contextValue}>
      {children}
      {showRecallResourcesDialog && req && (
        <RecallResourceRequestDialog
          resourceRequest={req}
          open={showRecallResourcesDialog}
          onCancelClick={handleCloseRecallResourceDialog}
          onMarkResourceRequestAsSubmitted={onMarkResourceRequestAsSubmitted}
          allocationId={
            resourceActionContextDetails.resourceAllocation &&
            resourceActionContextDetails.resourceAllocation.id
          }
        />
      )}

      {showReleaseResourcesDialog && (
        <ReleaseResourceDialog
          onReleaseResourceAllocationsForRequest={
            resourceActionContextDetails?.resourceRequestId &&
            releaseResourceAllocationsForRequest({
              resourceRequestId: resourceActionContextDetails?.resourceRequestId
            })
          }
          resourceRequest={req}
          allocation={resourceActionContextDetails.resourceAllocation}
          onReleaseResourceAllocation={
            resourceActionContextDetails.releaseResourceAllocation
          }
          open={showReleaseResourcesDialog}
          onCancelClick={handleCloseReleaseResourceDialog}
          canEditTask={project.permittedActionUris.includes(
            'urn:replicon:project-action:edit-tasks'
          )}
        />
      )}
    </ResourceActionsDialogContext.Provider>
  );
};

ResourceActionsDialogContextProvider.propTypes = {
  children: PropTypes.node,
  project: PropTypes.object,
  resourceRequests: PropTypes.array,
  maxSkillLevel: PropTypes.number
};

export default ResourceActionsDialogContextProvider;
